/**
 * `dayKey` is a calendar date encoded as the integer `yyyymmdd`, e.g. 20260813.
 *
 * Why an integer rather than a Date or an ISO string:
 *   - Day-range queries become integer comparisons, which IndexedDB indexes well.
 *   - It carries no time and no timezone, so it cannot drift when the user
 *     travels. Which calendar day an entry belongs to is decided once, at write
 *     time, by `blockGrid` — see 02-data-model.md §7.
 *
 * Everything here is pure calendar arithmetic with no timezone involvement.
 * `Date.UTC` is used purely as a proleptic Gregorian calculator; the instants
 * it produces are never exposed.
 */

export interface CalendarDate {
  year: number
  month: number // 1-12
  day: number // 1-31
}

export function encodeDayKey(year: number, month: number, day: number): number {
  return year * 10000 + month * 100 + day
}

export function decodeDayKey(dayKey: number): CalendarDate {
  return {
    year: Math.floor(dayKey / 10000),
    month: Math.floor(dayKey / 100) % 100,
    day: dayKey % 100,
  }
}

/** Rejects malformed keys and impossible dates such as 20260231. */
export function isValidDayKey(dayKey: number): boolean {
  if (!Number.isInteger(dayKey) || dayKey < 10000101 || dayKey > 99991231) return false

  const { year, month, day } = decodeDayKey(dayKey)
  if (month < 1 || month > 12 || day < 1 || day > 31) return false

  // Round-trip through the calendar: an overflowing day normalises to a
  // different date, which tells us the original was invalid.
  const t = new Date(Date.UTC(year, month - 1, day))
  return (
    t.getUTCFullYear() === year && t.getUTCMonth() === month - 1 && t.getUTCDate() === day
  )
}

export function addDays(dayKey: number, days: number): number {
  const { year, month, day } = decodeDayKey(dayKey)
  const t = new Date(Date.UTC(year, month - 1, day) + days * 86_400_000)
  return encodeDayKey(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())
}

/** Signed count of calendar days from `a` to `b`. */
export function daysBetween(a: number, b: number): number {
  const da = decodeDayKey(a)
  const db = decodeDayKey(b)
  const ta = Date.UTC(da.year, da.month - 1, da.day)
  const tb = Date.UTC(db.year, db.month - 1, db.day)
  return Math.round((tb - ta) / 86_400_000)
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(dayKey: number): number {
  const { year, month, day } = decodeDayKey(dayKey)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

/** "2026-08-13" — for export files and debugging, never for storage. */
export function dayKeyToISO(dayKey: number): string {
  const { year, month, day } = decodeDayKey(dayKey)
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function dayKeyFromISO(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) throw new Error(`Not an ISO date: ${iso}`)
  return encodeDayKey(Number(m[1]), Number(m[2]), Number(m[3]))
}
